import * as React from 'react';

import { ApplicationState, ConnectedReduxProps } from '../../../../store';
import { Button, Checkbox, InputLabel, Modal } from '@mui/material';
import {
  CommunicationOverviewDocumentItemForDisplay,
  CommunicationOverviewFreeTextItemForDisplay,
  CommunicationOverviewHonoraryNoteItemForDisplay,
  CommunicationOverviewReportItemForDisplay,
  CommunicationOverviewRimItemForDisplay,
  CommunicationOverviewSvsItemForDisplay
} from '../../../../store/details/overview/types';
import {
  askedAmountModalClose,
  changeAskedAmount,
  deleteItems,
  generateRimReports,
  generateSvsReports,
  getAskedAmount,
  getDocumentList,
  getHonoraryNoteList,
  getRimList,
  getSvsList,
  rimStatusChange,
  sendItems,
  sendItemsWithWarning,
  setAskedAmount,
  toggleAllFreeTextItem,
  toggleAllHonoraryNoteItem,
  toggleAllImageItem,
  toggleAllReportItem,
  toggleAllRimItem,
  toggleAllSvsItem,
  toggleFreeTextItem,
  toggleHonoraryNoteItem,
  toggleImageItem,
  toggleReportItem,
  toggleRimItem,
  toggleSelectAll,
  toggleSvsItem,
  deleteOverviewModalOpen,
  deleteOverviewModalClose,
} from '../../../../store/details/overview/actions';
import {
  getCommunicationOverviewFreeTextListForDisplay,
  getCommunicationOverviewHonoraryNoteListForDisplay,
  getCommunicationOverviewImageListForDisplay,
  getCommunicationOverviewOtherAttachmentListForDisplay,
  getCommunicationOverviewReportListForDisplay,
  getCommunicationOverviewRimListForDisplay,
  getCommunicationOverviewSvsListForDisplay,
  getToggleAllFreeTextIsChecked,
  getToggleAllHonoraryNoteIsChecked,
  getToggleAllImageIsChecked,
  getToggleAllReportIsChecked,
  getToggleAllRimIsChecked,
  getToggleAllSvsIsChecked
} from '../../../../store/details/overview/reducer';

import { BelfiusCurrencyInputInteger } from '../../../custom/belfius-currency-input-integer';
import CancelIcon from '@mui/icons-material/Cancel';
import { CaseResponse } from '../../../../store/case/model';
import { Dispatch } from 'redux';
import { Ii18n } from '../../../../store/utils/contracts/i18n.interface';
import { OverviewTableFreeText } from './overview-table-free-texts';
import { OverviewTableHonoraryNote } from './overview-table-honorary-notes';
import { OverviewTableImage } from './overview-table-images';
import { OverviewTableReport } from './overview-table-reports';
import { OverviewTableRim } from './overview-table-rims';
import { OverviewTableSvs } from './overview-table-svs';
import { Role } from '../../../../store/shared/model';
import { connect } from 'react-redux';
import { getDownloadLink } from '../../../../store/shared/actions';
import { getI18n } from '../../../../store/utils/i18n/strings.manager';
import styles from './overview.module.css';
import { DeleteOverviewModal } from 'src/components/common/deleteOverviewModal';
import { OverviewActionTypes } from '../../../../store/details/overview/types';

interface PropsFromState {
  readonly selectedCase: CaseResponse;
  readonly rimList: CommunicationOverviewRimItemForDisplay[];
  readonly svsList: CommunicationOverviewSvsItemForDisplay[];
  readonly freeTextList: CommunicationOverviewFreeTextItemForDisplay[];
  readonly imageList: CommunicationOverviewDocumentItemForDisplay[];
  readonly reportList: CommunicationOverviewReportItemForDisplay[];
  readonly honoraryNoteList: CommunicationOverviewHonoraryNoteItemForDisplay[];
  readonly toggleAllRimIsChecked: boolean;
  readonly toggleAllSvsIsChecked: boolean;
  readonly toggleAllFreeTextIsChecked: boolean;
  readonly toggleAllImageIsChecked: boolean;
  readonly toggleAllReportIsChecked: boolean;
  readonly toggleAllHonoraryNoteIsChecked: boolean;
  readonly currentLanguage: string;
  readonly isUIDisabled: boolean;
  readonly askedAmount: string;
  readonly roles: string[];
  readonly showAskedAmountModal: boolean;
  readonly showDeleteItemsModal: boolean;
  readonly caseCode: string;
  readonly instructionId: number;
}

interface PropsFromDispatch {
  readonly toggleRimItem: typeof toggleRimItem;
  readonly toggleAllRimItem: typeof toggleAllRimItem;
  readonly toggleSvsItem: typeof toggleSvsItem;
  readonly toggleAllSvsItem: typeof toggleAllSvsItem;
  readonly toggleFreeTextItem: typeof toggleFreeTextItem;
  readonly toggleAllFreeTextItem: typeof toggleAllFreeTextItem;
  readonly toggleImageItem: typeof toggleImageItem;
  readonly toggleAllImageItem: typeof toggleAllImageItem;
  readonly toggleReportItem: typeof toggleReportItem;
  readonly toggleAllReportItem: typeof toggleAllReportItem;
  readonly toggleHonoraryNoteItem: typeof toggleHonoraryNoteItem;
  readonly toggleAllHonoraryNoteItem: typeof toggleAllHonoraryNoteItem;
  readonly sendItems: typeof sendItems;
  readonly sendItemsWithWarning: typeof sendItemsWithWarning;
  readonly askedAmountModalClose: typeof askedAmountModalClose;
  readonly deleteItems: typeof deleteItems;
  readonly rimStatusChange: typeof rimStatusChange;
  readonly generateRimReports: typeof generateRimReports;
  readonly getDownloadLink: typeof getDownloadLink;
  readonly setAskedAmount: typeof setAskedAmount;
  readonly changeAskedAmount: typeof changeAskedAmount;
  readonly generateSvsReports: typeof generateSvsReports;
  readonly getRimList: typeof getRimList;
  readonly getSvsList: typeof getSvsList;
  readonly getDocumentList: typeof getDocumentList;
  readonly getHonoraryNoteList: typeof getHonoraryNoteList;
  readonly getAskedAmount: typeof getAskedAmount;
  readonly selectAll: typeof toggleSelectAll;
  readonly deleteOverviewModalOpen: typeof deleteOverviewModalOpen;
  readonly deleteOverviewModalClose: typeof deleteOverviewModalClose;

  // Add deleteModalClose with the correct type signature
  readonly deleteModalClose: () => () => { type: OverviewActionTypes.DELETE_OVERVIEW_MODAL_CLOSE };
}

interface Props {
  readonly handleDownloadClick: (amazonKey: string, filename: string) => void; // Handle independently
}

type AllProps = PropsFromState & PropsFromDispatch & ConnectedReduxProps & Props;

class Overview extends React.Component<AllProps> {
  private localization: Ii18n;

  constructor(props: AllProps) {
    super(props);
    this.localization = getI18n();
  }

  componentWillUnmount() {
    this.props.getRimList(this.props.instructionId);
    this.props.getSvsList(this.props.instructionId);
    this.props.getDocumentList(this.props.instructionId);
    this.props.getAskedAmount(this.props.instructionId);
    if (this.props.roles.includes(Role.CrInIn)) {
      this.props.getHonoraryNoteList(this.props.instructionId);
    }
  }

  componentDidUpdate(previousProps: AllProps) {
    if (previousProps.roles.length !== this.props.roles.length && this.props.roles.length > 0) {
      if (this.props.roles.includes(Role.CrInIn)) {
        this.props.getHonoraryNoteList(this.props.instructionId);
      }
    }
  }

  render() {
    return (
      <div style={{ height: '100%' }}>
        <div className={styles.container}>
          <div className={styles.rowContainer}>
            <div className={styles.twoColumnRow}>
              <div className={styles.inputRow}>
                <Checkbox
                  onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                    this.props.selectAll(checked, this.props.rimList, this.props.svsList, this.props.imageList, this.props.freeTextList, this.props.reportList, this.props.honoraryNoteList);
                  }}
                />
                <InputLabel className={styles.inputLabel}>{this.localization.get('overview.askedAmount')}</InputLabel>
                <BelfiusCurrencyInputInteger className={styles.input}
                  value={this.props.askedAmount}
                  placeholder={this.localization.get('details.rim.valueAndCause.value')}
                  onChange={(value: string) => {
                    const val = value === '' ? '' : parseFloat(value.replace('.', '').replace(',', '.')).toString();
                    this.props.changeAskedAmount(val);
                  }}
                  onBlur={(value: string) => {
                    const val = value === '' ? '' : parseFloat(value.replace('.', '').replace(',', '.')).toString();
                    this.props.setAskedAmount(this.props.instructionId, val);
                  }}
                />
              </div>
              <div className={styles.actionRow}>
                <Button variant='contained'
                style={{backgroundColor: 'seagreen', color: 'white'}}
                disabled={this.props.selectedCase.blocked}
                onClick={() => {
                  if (this.props.selectedCase.blocked) { return; }
                  this.props.sendItemsWithWarning(this.props.caseCode); }}>
                  {this.localization.get('shared.send')}
                </Button>
                <Button variant='contained' color='secondary'
                disabled={this.props.selectedCase.blocked}
                onClick={() => {
                   if (this.props.selectedCase.blocked) { return; }
                     this.props.deleteOverviewModalOpen(this.props.caseCode); }}>
                  {this.localization.get('shared.delete')}
                </Button>
              </div>
            </div>
          </div>
          <div className={styles.container2}>
            {this.props.rimList.length > 0 &&
              <div className={styles.rowContainer}>
                <OverviewTableRim
                  rimStatusChange={this.props.rimStatusChange}
                  title={this.localization.get('overview.rimTitle')}
                  currentLanguage={this.props.currentLanguage}
                  rows={this.props.rimList}
                  toggleItem={this.props.toggleRimItem}
                  toggleAllChecked={this.props.toggleAllRimIsChecked}
                  togleAll={this.props.toggleAllRimItem}
                  generateRimReports={this.props.generateRimReports}
                  caseCode={this.props.caseCode}
                  handleDownloadClick={this.props.handleDownloadClick}
                  caseBlocked={this.props.selectedCase.blocked} />
              </div>
            }
            {this.props.svsList.length > 0 &&
              <div className={styles.rowContainer}>
                <OverviewTableSvs
                  title={this.localization.get('overview.svsTitle')}
                  currentLanguage={this.props.currentLanguage}
                  rows={this.props.svsList}
                  toggleItem={this.props.toggleSvsItem}
                  toggleAllChecked={this.props.toggleAllSvsIsChecked}
                  togleAll={this.props.toggleAllSvsItem}
                  generateSvsReports={this.props.generateSvsReports}
                  caseCode={this.props.caseCode}
                  handleDownloadClick={this.props.handleDownloadClick}
                  caseBlocked={this.props.selectedCase.blocked} />
              </div>
            }
            {this.props.imageList.length > 0 &&
              <div className={styles.rowContainer}>
                <OverviewTableImage
                  title={this.localization.get('overview.imageTitle')}
                  currentLanguage={this.props.currentLanguage}
                  rows={this.props.imageList}
                  toggleItem={this.props.toggleImageItem}
                  toggleAllChecked={this.props.toggleAllImageIsChecked}
                  togleAll={this.props.toggleAllImageItem}
                  handleDownloadClick={this.props.handleDownloadClick}
                  caseBlocked={this.props.selectedCase.blocked} />
              </div>
            }
            {this.props.freeTextList.length > 0 &&
              <div className={styles.rowContainer}>
                <OverviewTableFreeText
                  title={this.localization.get('overview.freeTextTitle')}
                  currentLanguage={this.props.currentLanguage}
                  rows={this.props.freeTextList}
                  toggleItem={this.props.toggleFreeTextItem}
                  toggleAllChecked={this.props.toggleAllFreeTextIsChecked}
                  togleAll={this.props.toggleAllFreeTextItem}
                  caseBlocked={this.props.selectedCase.blocked} />
              </div>
            }
            {this.props.reportList.length > 0 &&
              <div className={styles.rowContainer}>
                <OverviewTableReport
                  title={this.localization.get('overview.reportTitle')}
                  currentLanguage={this.props.currentLanguage}
                  rows={this.props.reportList}
                  toggleItem={this.props.toggleReportItem}
                  toggleAllChecked={this.props.toggleAllReportIsChecked}
                  togleAll={this.props.toggleAllReportItem}
                  handleDownloadClick={this.props.handleDownloadClick}
                  caseBlocked={this.props.selectedCase.blocked} />
              </div>
            }
            {this.props.roles.includes(Role.CrInIn) ?
              this.props.honoraryNoteList.length > 0 && <div className={styles.rowContainer}>
                <OverviewTableHonoraryNote
                  title={this.localization.get('overview.honoraryNote')}
                  currentLanguage={this.props.currentLanguage}
                  rows={this.props.honoraryNoteList}
                  toggleItem={this.props.toggleHonoraryNoteItem}
                  toggleAllChecked={this.props.toggleAllHonoraryNoteIsChecked}
                  togleAll={this.props.toggleAllHonoraryNoteItem}
                  caseBlocked={this.props.selectedCase.blocked} />
              </div> : ''}
          </div>
          <Modal
            aria-labelledby='simple-modal-title'
            aria-describedby='simple-modal-description'
            open={this.props.showAskedAmountModal}
            onClose={() => this.props.askedAmountModalClose()}
          >
            <div className={styles.askedAmountModal}>
              <div className={styles.rowCenter}>
                {this.localization.get('overview.asked.amount.modal.message')}
              </div>
              <br />
              <div className={styles.rowCenter}>
                <Button
                  variant='contained'
                  className={styles.button}
                  onClick={() => this.props.askedAmountModalClose()} >
                  {this.localization.get('shared.cancel.button')}
                  <CancelIcon className={styles.rightIcon} />
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  className={styles.button}
                  disabled={this.props.selectedCase.blocked}
                  onClick={() => {
                    if (this.props.selectedCase.blocked) { return; }
                    this.props.sendItems(this.props.caseCode);
                    this.props.askedAmountModalClose();
                  }} >
                  {this.localization.get('shared.ok.button')}
                </Button>
              </div>
            </div>
          </Modal>
          <DeleteOverviewModal
            caseCode={this.props.selectedCase.caseCode}
            showDeleteModal={this.props.showDeleteItemsModal}
            deleteModalClose={this.props.deleteModalClose}
            deleteItems={this.props.deleteItems}
        ></DeleteOverviewModal>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ caseState, details: { detailsOverview, detailsRim, detailsSvs, detailsCommentOrReport, detailsImages, detailsOtherAttachments, detailsHonoraryNote }, shared }: ApplicationState): PropsFromState => ({
  // tslint:disable-next-line:no-non-null-assertion
  selectedCase: caseState.selectedCase,
  rimList: getCommunicationOverviewRimListForDisplay({ ...detailsRim, ...detailsOverview }),
  svsList: getCommunicationOverviewSvsListForDisplay({ ...detailsSvs, ...detailsOverview }),
  freeTextList: getCommunicationOverviewFreeTextListForDisplay({ ...detailsCommentOrReport, ...detailsOverview }),
  honoraryNoteList: getCommunicationOverviewHonoraryNoteListForDisplay({ ...detailsHonoraryNote, ...detailsOverview }),
  // CHECK SLECTOR LIKE THIS FOR PERFORMANCE!
  imageList: getCommunicationOverviewImageListForDisplay({ ...detailsOverview, ...detailsImages }).concat(getCommunicationOverviewOtherAttachmentListForDisplay({ ...detailsOverview, ...detailsOtherAttachments })),

  reportList: getCommunicationOverviewReportListForDisplay({ ...detailsCommentOrReport, ...shared, ...detailsOverview }),
  toggleAllRimIsChecked: getToggleAllRimIsChecked(detailsOverview),
  toggleAllSvsIsChecked: getToggleAllSvsIsChecked(detailsOverview),
  toggleAllFreeTextIsChecked: getToggleAllFreeTextIsChecked(detailsOverview),
  toggleAllImageIsChecked: getToggleAllImageIsChecked(detailsOverview),
  toggleAllReportIsChecked: getToggleAllReportIsChecked(detailsOverview),
  toggleAllHonoraryNoteIsChecked: getToggleAllHonoraryNoteIsChecked(detailsOverview),
  currentLanguage: shared.currentLanguage,
  isUIDisabled: shared.isUIDisabled,
  askedAmount: detailsOverview.askedAmount,
  showAskedAmountModal: detailsOverview.showAskedAmountModal,
  showDeleteItemsModal: detailsOverview.showDeleteItemsModal,
  roles: shared.roles,
  caseCode: caseState.selectedCase.caseCode,
  instructionId: caseState.selectedCase.instructionId,
});

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
  toggleRimItem: (id: number) => dispatch(toggleRimItem(id)),
  toggleAllRimItem: (checked: boolean) => dispatch(toggleAllRimItem(checked)),
  toggleSvsItem: (id: number) => dispatch(toggleSvsItem(id)),
  toggleAllSvsItem: (checked: boolean) => dispatch(toggleAllSvsItem(checked)),
  toggleFreeTextItem: (id: number) => dispatch(toggleFreeTextItem(id)),
  toggleAllFreeTextItem: (checked: boolean) => dispatch(toggleAllFreeTextItem(checked)),
  toggleImageItem: (id: number) => dispatch(toggleImageItem(id)),
  toggleAllImageItem: (checked: boolean) => dispatch(toggleAllImageItem(checked)),
  toggleReportItem: (id: number) => dispatch(toggleReportItem(id)),
  toggleAllReportItem: (checked: boolean) => dispatch(toggleAllReportItem(checked)),
  toggleHonoraryNoteItem: (id: number) => dispatch(toggleHonoraryNoteItem(id)),
  toggleAllHonoraryNoteItem: (checked: boolean) => dispatch(toggleAllHonoraryNoteItem(checked)),
  sendItems: (caseCode: string) => dispatch(sendItems(caseCode)),
  sendItemsWithWarning: (caseCode: string) => dispatch(sendItemsWithWarning(caseCode)),
  askedAmountModalClose: () => dispatch(askedAmountModalClose()),
  deleteItems: (caseCode: string) => dispatch(deleteItems(caseCode)),
  deleteOverviewModalOpen: (caseCode: string) => dispatch(deleteOverviewModalOpen(caseCode)),
  deleteOverviewModalClose: () => dispatch(deleteOverviewModalClose()),

  // Ensure deleteModalClose returns the expected type
  deleteModalClose: () => () => dispatch(deleteOverviewModalClose()),
  rimStatusChange: (id: number, status: string) => dispatch(rimStatusChange(id, status)),
  generateRimReports: (caseCode: string, rimHeaderIds: number[]) => dispatch(generateRimReports(caseCode, rimHeaderIds)),
  setAskedAmount: (instructionId: number, amount: string) => dispatch(setAskedAmount(instructionId, amount)),
  changeAskedAmount: (amount: string) => dispatch(changeAskedAmount(amount)),
  generateSvsReports: (caseCode: string, svsHeaderIds: number[]) => dispatch(generateSvsReports(caseCode, svsHeaderIds)),
  getRimList: (instructionId: number) => dispatch(getRimList(instructionId)),
  getSvsList: (instructionId: number) => dispatch(getSvsList(instructionId)),
  getDocumentList: (instructionId: number) => dispatch(getDocumentList(instructionId)),
  getHonoraryNoteList: (instructionId: number) => dispatch(getHonoraryNoteList(instructionId)),
  getAskedAmount: (instructionId: number) => dispatch(getAskedAmount(instructionId)),
  getDownloadLink: (amazonKey: string, url: string, content: string, filename: string, onClickLink) => dispatch(getDownloadLink(amazonKey, url, content, filename, onClickLink)),
  selectAll: (checked: boolean, rimList: CommunicationOverviewRimItemForDisplay[], svsList: CommunicationOverviewSvsItemForDisplay[],
    documentList: CommunicationOverviewDocumentItemForDisplay[], freeTextList: CommunicationOverviewFreeTextItemForDisplay[],
    reportList: CommunicationOverviewReportItemForDisplay[], honoraryNoteList: CommunicationOverviewHonoraryNoteItemForDisplay[]) => dispatch(toggleSelectAll(checked, rimList, svsList, documentList, freeTextList, reportList, honoraryNoteList))
});

export default connect(mapStateToProps, mapDispatchToProps)(Overview);
